import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { API_BASE_URL } from '../api';
import { useToast } from '../components/Toast';
import { setStoredUser } from '../utils/safeStorage';

const LoginPage = ({ navigate, onLogin }) => {
  const toast = useToast();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/auth/login/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      let data = null;
      try {
        data = await response.json();
      } catch {
        throw new Error('Invalid response from server');
      }

      if (response.ok) {
        setStoredUser(data.user);
        if (data.tokens?.access) {
          localStorage.setItem('authToken', data.tokens.access);
        }
        onLogin(data.user);
        toast.success('Welcome back');
        navigate('portal');
      } else {
        setError(data.error || data.detail || 'Login failed. Please check your details.');
      }
    } catch (err) {
      const msg = err.message === 'Failed to fetch'
        ? 'Network error. Please check your connection and try again.'
        : (err.message || 'Network error. Please try again.');
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-cream min-h-screen">
      <Navbar navigate={navigate} />
      
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-cream via-white to-sand py-12 px-4 pt-28">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-xl border border-sand p-5 sm:p-8">
            <div className="text-center mb-8">
              <div className="text-6xl mb-4">🕌</div>
              <h2 className="text-2xl sm:text-3xl font-bold text-ink mb-2">Welcome Back</h2>
              <p className="text-ink/65">Login to your account</p>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-ink/80 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-4 py-3 border border-sand rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-ink/80 mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="w-full px-4 py-3 border border-sand rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent pr-12"
                    placeholder="••••••••"
                  />
                  <button type="button" onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-ink/50">
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => navigate('forgot-password')}
                  className="text-sm text-primary hover:text-secondary">
                  Forgot Password?
                </button>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full gradient-gold text-ink py-3 rounded-md font-bold hover:opacity-90 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed">
                {loading ? 'Logging in...' : 'Login'}
              </button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-ink/65">
                Don't have an account?{' '}
                <button
                  onClick={() => navigate('register')}
                  className="text-primary font-semibold hover:text-secondary">
                  Register Now
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
      <Footer navigate={navigate} />
    </div>
  );
};

export default LoginPage;
